import React, { useMemo, useRef, useState } from 'react';
import { SnippetInputContainerProps } from './snippetInputProps';
import { ReportingContainerBaseConfig } from '../../../base';
import { SnippetInputController } from './snippetInputInterface';
import { assertNever } from '@varos/util-typescript';
import { SnippetInputNewContainerProps } from './new';
import { SnippetInputEditContainerProps } from './edit';
import { useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { SnippetInputLabelContainerProps } from './label';

export function createSnippetInputContainer(
    config: ReportingContainerBaseConfig & {
        Container: {
            Snippet: {
                Edit: React.FC<SnippetInputEditContainerProps>;
                New: React.FC<SnippetInputNewContainerProps>;
                Label: React.FC<SnippetInputLabelContainerProps>;
            };
        };
    },
    controller: SnippetInputController
): React.FC<SnippetInputContainerProps> {
    const {
        context: { useContext },
        Container: {
            Snippet: { Edit: EditContainer, New: NewContainer, Label: LabelContainer },
        },
    } = config;

    return ({ closeOnEscape = true, ...containerProps }) => {
        const {
            as: {
                Input: SnippetInput,
                Placeholder: SnippetPlaceholder,
                Label: SnippetLabel,
                Root: SnippetRoot,
            },
        } = containerProps;
        const context = useContext();
        const containerRef = useRef<HTMLDivElement>(null);

        const disclosure = useDisclosure({
            isOpen: containerProps.isOpen,
            onOpen: containerProps.onOpen,
            onClose: containerProps.onClose,
        });

        // useOutsideClick({
        //     ref: containerRef,
        //     handler: () => {
        //         disclosure.onClose();
        //     },
        // });

        const props = controller.useProps(
            context,
            {},
            {
                disclosure,
                containerRef,
                closeOnEscape,
                value: containerProps.value,
                onCreate(value) {
                    containerProps.onCreate(value);
                },
                onUpdate(value) {
                    containerProps.onUpdate(value);
                },
                onClear() {
                    containerProps.onClear();
                },
            }
        );

        if (props.view.kind === 'empty') {
            return <SnippetPlaceholder {...props.view} />;
        }
        if (props.view.kind === 'edit' || props.view.kind === 'new') {
            return (
                <SnippetRoot element={props.root.element}>
                    {props.view.kind === 'edit' && (
                        <EditContainer as={SnippetInput} {...props.view} />
                    )}
                    {props.view.kind === 'new' && (
                        <NewContainer as={SnippetInput} {...props.view} />
                    )}
                </SnippetRoot>
            );
        }
        if (props.view.kind === 'label') {
            return <LabelContainer as={SnippetLabel} {...props.view} />;
        }

        assertNever(props.view);
    };
}
